// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, ChangePasswordData } from '../types';
import { usersApi } from '../services/api';

interface AuthContextType {
  user: User | null;
  currentUser: User | null; // Alias for user for backward compatibility
  isAuthenticated: boolean;
  isLoading: boolean;
  loading: boolean; // Alias for isLoading for backward compatibility
  hasRole: (role: string | string[]) => boolean;
  login: (email: string, password: string) => Promise<{ redirectUrl?: string; message?: string }>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  updateProfile: (data: { name: string; email: string }) => Promise<void>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is already logged in on initial load
  useEffect(() => {
    let isMounted = true;
    
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        console.log('Auth check - Token exists:', !!token);
        
        if (token) {
          try {
            // Use getProfile instead of getById to get the current user's profile
            const userProfile = await usersApi.getProfile();
            console.log('Auth check - User profile loaded:', userProfile);
            
            if (isMounted) {
              setUser(userProfile);
            }
          } catch (profileError) {
            console.error('Failed to load user profile:', profileError);
            if (isMounted) {
              // Clear invalid tokens
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              setUser(null);
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (isMounted) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkAuth();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }

      const data = await response.json();
      
      // Store tokens
      localStorage.setItem('access_token', data.token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      
      // Set user data
      setUser(data.user);
      
      // Return redirect information from backend
      return {
        redirectUrl: data.redirect_url,
        message: data.message
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    window.location.href = '/login';
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/auth/refresh`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${refreshToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      return true;
    } catch (error) {
      console.error('Refresh token failed:', error);
      logout();
      return false;
    }
  };

  const updateProfile = async (data: { name: string; email: string }) => {
    try {
      const updatedUser = await usersApi.updateProfile(data);
      setUser(updatedUser);
    } catch (error) {
      console.error('Update profile failed:', error);
      throw error;
    }
  };

  const changePassword = async (data: ChangePasswordData) => {
    try {
      await usersApi.changePassword(data);
    } catch (error) {
      console.error('Change password failed:', error);
      throw error;
    }
  };

  const hasRole = useCallback((role: string | string[]) => {
    if (!user?.role) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  }, [user]);

  const value = {
    user,
    currentUser: user, // Alias for backward compatibility
    isAuthenticated: !!user,
    isLoading,
    loading: isLoading, // Alias for backward compatibility
    hasRole,
    login,
    logout,
    refreshToken,
    updateProfile,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};